import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import { InputPercent } from "@/components/_pages/ModelPage/Sidebar/InputPercent";
import { InputSelect } from "@/components/_pages/ModelPage/Sidebar/InputSelect";
import { InputToggle } from "@/components/_pages/ModelPage/Sidebar/InputToggle";
import { InputYears } from "@/components/_pages/ModelPage/Sidebar/InputYears";
import {
  includeSellerNoteInHistoricalAtom,
  sellersNoteAmortizationScheduleAtom,
  sellersNoteHasStandbyProvisionAtom,
  sellersNoteInterestRateAtom,
  sellersNoteStandbyAccrueInterestInFullStandbyAtom,
  sellersNoteStandbyIsFullStandbyAtom,
  sellersNoteStandbyPeriodAtom,
  sellersNoteStandbyPeriodErrorMessageAtom,
  sellersNoteTermLengthAtom,
} from "@/financeModels/owasco/general";
import { numberToPercentInput, percentInputToNumber } from "@/utils/format";
import { lazyReactFallback } from "@/utils/lazy";
import { toNumberOrNull } from "@/utils/math";
import { Alert, Divider, Stack, Text } from "@mantine/core";
import { useAtom, useAtomValue } from "jotai";
import { ChangeEvent } from "react";
import { TbId } from "react-icons/tb";

const SellersNoteInterestRateExplainer = lazyReactFallback(
  () => import("@/docs/pages/model/sidebar/sellersNoteInterestRate.mdx"),
);
const SellersNoteAmortScheduleExplainer = lazyReactFallback(
  () => import("@/docs/pages/model/sidebar/sellersNoteAmortSchedule.mdx"),
);
// const SellersNoteLoanStartDate = lazyReactFallback(
//   () => import("@/docs/pages/model/sidebar/sellersNoteLoanStartDate.mdx"),
// );

const SellersNoteHasStandbyProvisionExplainer = lazyReactFallback(
  () => import("@/docs/pages/model/sidebar/sellersNoteHasStandbyProvision.mdx"),
);

const SellersNoteStandbyPeriodExplainer = lazyReactFallback(
  () => import("@/docs/pages/model/sidebar/sellersNoteStandbyPeriod.mdx"),
);

const SellersNoteStandbyIsFullStandbyExplainer = lazyReactFallback(
  () =>
    import("@/docs/pages/model/sidebar/sellersNoteStandbyIsFullStandby.mdx"),
);

const SellersNoteStandbyAccrueInterestInFullStandbyExplainer =
  lazyReactFallback(
    () =>
      import(
        "@/docs/pages/model/sidebar/sellersNoteStandbyAccrueInterestInFullStandby.mdx"
      ),
  );

const Panel = () => {
  const [interestRate, setInterestRate] = useAtom(sellersNoteInterestRateAtom);
  const [schedule, setSchedule] = useAtom(sellersNoteAmortizationScheduleAtom);
  const [termLength, setTermLength] = useAtom(sellersNoteTermLengthAtom);
  //const [loanStartDate, setLoanStartDate] = useAtom(sellersNoteStartDateAtom);
  const [hasStandbyProvision, setHasStandbyProvision] = useAtom(
    sellersNoteHasStandbyProvisionAtom,
  );
  const [standbyPeriod, setStandbyPeriod] = useAtom(
    sellersNoteStandbyPeriodAtom,
  );
  const [isFullStandby, setIsFullStandby] = useAtom(
    sellersNoteStandbyIsFullStandbyAtom,
  );
  const [accrueInterestInFullStandby, setAccrueInterestInFullStandby] = useAtom(
    sellersNoteStandbyAccrueInterestInFullStandbyAtom,
  );

  const standbyPeriodErrorMessage = useAtomValue(
    sellersNoteStandbyPeriodErrorMessageAtom,
  );
  const [includeSellerNoteInHistorical, setIncludeSellerNoteInHistorical] =
    useAtom(includeSellerNoteInHistoricalAtom);

  return (
    <Stack gap={4}>
      <InputPercent
        value={numberToPercentInput(interestRate)}
        onChange={(value) => setInterestRate(percentInputToNumber(value))}
        label={
          <LabelExplainer
            label="Interest Rate"
            explanation={<SellersNoteInterestRateExplainer />}
          />
        }
      />

      <InputYears
        value={toNumberOrNull(termLength) || ""}
        onChange={(value) => setTermLength(toNumberOrNull(value))}
        label={
          <LabelExplainer
            label="Term Length"
            explanation={
              <Doc path="@/docs/pages/model/sidebar/sellersNoteConventionalTermLength.mdx" />
            }
          />
        }
      />

      <InputYears
        value={toNumberOrNull(schedule) || ""}
        onChange={(value) => setSchedule(toNumberOrNull(value))}
        label={
          <LabelExplainer
            label="Amortization Schedule"
            explanation={<SellersNoteAmortScheduleExplainer />}
          />
        }
      />

      <InputToggle
        label={
          <LabelExplainer
            label="Standby Provision"
            explanation={<SellersNoteHasStandbyProvisionExplainer />}
          />
        }
        checked={hasStandbyProvision}
        onChange={(event) =>
          setHasStandbyProvision(
            (event as ChangeEvent<HTMLInputElement>).currentTarget.checked,
          )
        }
      />

      {hasStandbyProvision && (
        <>
          <InputYears
            value={Number(standbyPeriod)}
            onChange={(value) => setStandbyPeriod(Number(value))}
            label={
              <LabelExplainer
                label="Standby Period"
                explanation={<SellersNoteStandbyPeriodExplainer />}
              />
            }
          />
          {standbyPeriodErrorMessage && (
            <Alert variant="light" color="red" ta={"center"}>
              {standbyPeriodErrorMessage}
            </Alert>
          )}
          <InputSelect
            labelWidth={120}
            label={
              <LabelExplainer
                label="Standby Type"
                explanation={<SellersNoteStandbyIsFullStandbyExplainer />}
              />
            }
            data={[
              { value: "full", label: "Full Standby" },
              { value: "partial", label: "Partial Standby (I/O) " },
            ]}
            value={isFullStandby ? "full" : "partial"}
            onChange={(value) => setIsFullStandby(value === "full")}
          />
          {isFullStandby && (
            <InputToggle
              label={
                <LabelExplainer
                  label="Accrue Interest In Full Standby"
                  explanation={
                    <SellersNoteStandbyAccrueInterestInFullStandbyExplainer />
                  }
                />
              }
              checked={accrueInterestInFullStandby}
              onChange={(event) =>
                setAccrueInterestInFullStandby(
                  (event as ChangeEvent<HTMLInputElement>).currentTarget
                    .checked,
                )
              }
            />
          )}
        </>
      )}
      <Divider my="sm" />

      <Text size="sm" fw={"bold"} mb="sm">
        Historical Adjustments
      </Text>

      <InputToggle
        labelWidth={240}
        label={
          <LabelExplainer
            label="Use Seller's Note in Historicals?"
            explanation={
              <Doc path="@/docs/pages/model/sidebar/sellersNoteIncludeInHistorical.mdx" />
            }
          />
        }
        checked={includeSellerNoteInHistorical}
        onChange={(event) => {
          setIncludeSellerNoteInHistorical(
            (event as ChangeEvent<HTMLInputElement>).currentTarget.checked,
          );
        }}
      />
    </Stack>
  );
};

export const ModuleSellersNote = {
  key: "Seller's Note",
  icon: <TbId />,
  control: "Seller's Note",
  panel: <Panel />,
};
